import { Button, Divider, Paper } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { debounce } from 'lodash-es';
import React from 'react';
// react-awesome-query-builder
import {
  Builder,
  BuilderProps,
  Config,
  Query,
  Utils as QbUtils,
} from 'react-awesome-query-builder';
import {
  Fields,
  ImmutableTree,
  JsonGroup,
  JsonRule,
  JsonRuleGroup,
  JsonRuleGroupExt,
} from 'react-awesome-query-builder';
import MaterialConfig from 'react-awesome-query-builder/lib/config/material';

import { AudienceSegmentLogicAnd, AudienceSegmentLogicNot } from '~/src/dash/api/console-api';
// custom helper components
import MaterialButton from '~/src/dash/components/segment-logic-query-builder/components/MaterialButton';
import MaterialConjs from '~/src/dash/components/segment-logic-query-builder/components/MaterialConjs';
import { IStore } from '~/src/dash/reducers/store';
import { IUserStateProps, IWithStylesProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';

interface IItem {
  label: string;
  value: string;
}

interface ISegmentLogicQueryBuilderProps extends IWithStylesProps, IUserStateProps {
  oldLogic: string;
  items: IItem[];
  queryJson: string;
  onUpdate?: (query: string, queryJson: string) => void;
}

const startingConfig: Config = {
  ...MaterialConfig,
  settings: {
    ...MaterialConfig.settings,
    defaultSelectWidth: '50em',
    addGroupLabel: 'Group',
    addRuleLabel: 'Segment',
    // @ts-ignore
    defaultGroupConjunction: 'OR',
    renderOperator: () => {
      // do not render operator selector
      // defaultOperator must be defined in the fields config
      return <></>;
    },
    renderButton: MaterialButton,
    renderConjs: MaterialConjs,
  },
  widgets: {
    ...MaterialConfig.widgets,
    boolean: {
      ...MaterialConfig.widgets.boolean,
      factory: () => {
        // do not render boolean widget
        // defaultValue must be defined in the fields config
        return <></>;
      },
    },
  },
};

const createConfigFields = (items: IItem[]): Fields => {
  const fields: Fields = {};
  items.forEach((item: IItem) => {
    fields[item.value] = {
      label: item.label,
      type: 'boolean',
      operators: ['equal'],
      defaultOperator: 'equal',
      valueSources: ['value'],
      defaultValue: true,
    };
  });
  return fields;
};

// Recurrsively searches through the tree to find all used fields
const getUsedFields = (root: JsonGroup | JsonRule | JsonRuleGroup | JsonRuleGroupExt): string[] => {
  const usedFields: string[] = [];

  if (root.type === 'rule' || root.type === 'rule_group') {
    if (root.properties && root.properties.field) {
      usedFields.push(root.properties.field);
    }
  }

  if (root.type === 'rule') {
    return usedFields;
  }

  if (root.children1) {
    for (const id in root.children1) {
      if (Object.prototype.hasOwnProperty.call(root.children1, id)) {
        const fields = getUsedFields(root.children1[id]);
        usedFields.push(...fields);
      }
    }
  }

  return usedFields;
};

// returns a new tree with the missing items added
const addMissingFields = (items: IItem[], tree: ImmutableTree, config: Config): ImmutableTree => {
  const jsonTree = QbUtils.getTree(tree);
  const usedFields = getUsedFields(jsonTree);
  const missingItems: IItem[] = items.filter((item) => !usedFields.includes(item.value));
  if (!jsonTree.children1) {
    jsonTree.children1 = {};
  }
  missingItems.forEach((item: IItem) => {
    if (jsonTree.children1) {
      jsonTree.children1[QbUtils.uuid()] = {
        type: 'rule',
        properties: {
          field: item.value,
          operator: 'equal',
          value: [true],
          valueSrc: ['value'],
          valueType: ['boolean'],
        },
      };
    }
  });

  return QbUtils.checkTree(QbUtils.loadTree(jsonTree), config);
};

export const SegmentLogicQueryBuilder = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
      };
    },
  },
  class extends React.Component<ISegmentLogicQueryBuilderProps> {
    state = {
      items: Array<IItem>(),
      config: startingConfig,
      tree: ((): ImmutableTree => {
        return QbUtils.checkTree(
          QbUtils.loadTree({
            id: QbUtils.uuid(),
            type: 'group',
            properties: {
              conjunction: 'OR',
            },
          }),
          startingConfig,
        );
      })(),
    };

    updateResult = debounce((immutableTree: ImmutableTree, config: Config) => {
      this.setState({ tree: immutableTree, config });

      if (this.props.onUpdate) {
        const query = QbUtils.queryString(immutableTree, config);
        const queryJson = JSON.stringify(QbUtils.getTree(immutableTree));
        this.props.onUpdate(query, queryJson);
      }
    }, 1000);

    componentDidMount() {
      const { items, queryJson, oldLogic } = this.props;
      const config = this.state.config;
      config.fields = createConfigFields(items);

      let tree = this.state.tree;
      if (queryJson) {
        // load existing query
        tree = QbUtils.checkTree(QbUtils.loadTree(JSON.parse(queryJson)), config);
      } else {
        tree = QbUtils.checkTree(
          QbUtils.loadTree({
            id: QbUtils.uuid(),
            type: 'group',
            properties: {
              conjunction: oldLogic === AudienceSegmentLogicAnd ? 'AND' : 'OR',
              not: oldLogic === AudienceSegmentLogicNot,
            },
          }),
          config,
        );
      }

      this.setState({
        tree: addMissingFields(items, tree, config),
        config,
      });
    }

    componentDidUpdate(prevProps: ISegmentLogicQueryBuilderProps) {
      if (prevProps.items.length !== this.props.items.length) {
        const { items } = this.props;
        const { tree, config } = this.state;
        config.fields = createConfigFields(items);

        this.setState({
          tree: addMissingFields(items, tree, config),
          config,
        });
      }
    }

    render() {
      const { user, items } = this.props;
      const { config, tree } = this.state;

      if (items.length === 0) {
        return <></>;
      }

      return (
        <>
          <Query
            {...config}
            value={tree}
            onChange={this.onChange}
            renderBuilder={this.renderBuilder}
          />
          <div>
            <Button
              variant='contained'
              size='small'
              onClick={() => {
                this.setState({ tree: addMissingFields(items, tree, config) });
              }}
            >
              <AddIcon />
              Add Missing Fields
            </Button>
          </div>
          {hasRoleSuperUser(user.user) && (
            <Paper variant='outlined' style={{ padding: '5px 10px', margin: '10px 0' }}>
              <div>Debug *</div>
              <Divider />
              <div>Items: {items.map((item) => item.value).join(', ')}</div>
              <Divider />
              <div>Query: {QbUtils.queryString(tree, config)}</div>
            </Paper>
          )}
        </>
      );
    }

    renderBuilder = (props: BuilderProps) => (
      <div className='query-builder-container'>
        <div className='query-builder' style={{ margin: '1rem 0' }}>
          <Builder {...props} />
        </div>
      </div>
    );

    onChange = (immutableTree: ImmutableTree, config: Config) => {
      this.updateResult(immutableTree, config);
    };
  },
);
